import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Search, FolderPlus, Folder, FileText, Trash2, Plus, Filter, LayoutGrid, KanbanSquare } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = [
  { key: "draft", label: "Draft", color: "bg-slate-500" },
  { key: "ready", label: "Ready", color: "bg-sky-500" },
  { key: "submitted", label: "Submitted", color: "bg-indigo-500" },
  { key: "interview", label: "Interview", color: "bg-violet-500" },
  { key: "offer", label: "Offer", color: "bg-orange-500" },
  { key: "placed", label: "Placed", color: "bg-emerald-500" },
  { key: "hold", label: "Hold", color: "bg-amber-500" },
  { key: "rejected", label: "Rejected", color: "bg-rose-500" },
];

export default function Organizer() {
  const [resumes, setResumes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [folderId, setFolderId] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [newFolder, setNewFolder] = useState("");
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("grid"); // grid | kanban
  const [levelFilter, setLevelFilter] = useState("all");
  const navigate = useNavigate();

  const refresh = async () => {
    const params = {};
    if (q) params.q = q;
    if (tag) params.tag = tag;
    if (folderId) params.folder_id = folderId;
    const [r1, r2] = await Promise.all([api.get("/resumes", { params }), api.get("/folders")]);
    setResumes(r1.data); setFolders(r2.data);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [q, tag, folderId]);

  const createFolder = async () => {
    if (!newFolder) return;
    await api.post("/folders", { name: newFolder });
    setNewFolder(""); setOpen(false);
    toast.success("Folder created");
    refresh();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this resume?")) return;
    await api.delete(`/resumes/${id}`);
    toast.success("Deleted"); refresh();
  };

  const setStatus = async (id, s, e) => {
    if (e) e.stopPropagation();
    try {
      await api.patch(`/resumes/${id}/status`, { pipeline_status: s });
      toast.success(`Moved to ${s.toUpperCase()}`);
      refresh();
    } catch { toast.error("Could not update status"); }
  };

  const allTags = Array.from(new Set(resumes.flatMap((r) => r.tags || [])));

  const visibleResumes = resumes.filter((r) => {
    if (statusFilter !== "all" && (r.pipeline_status || "draft") !== statusFilter) return false;
    if (levelFilter !== "all" && (r.experience_level || "") !== levelFilter) return false;
    return true;
  });

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold">Resume Library</div>
          <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">Organizer</h1>
          <p className="text-muted-foreground mt-1 text-sm">Search, filter and manage every standardized resume.</p>
        </div>
        <Button onClick={() => navigate("/upload")} className="lift gap-2" data-testid="organizer-new-button"><Plus className="size-4" /> New resume</Button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Folder rail */}
        <aside className="col-span-12 md:col-span-3">
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Folders</div>
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild>
                    <Button size="icon" variant="ghost" data-testid="folder-new-button"><FolderPlus className="size-4" /></Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>New folder</DialogTitle></DialogHeader>
                    <div className="grid gap-2 py-2">
                      <Label className="text-xs font-semibold">Name</Label>
                      <Input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} placeholder="e.g. Sales Roles" data-testid="folder-name-input" />
                    </div>
                    <DialogFooter>
                      <Button onClick={createFolder} data-testid="folder-create-submit">Create</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <button onClick={() => setFolderId("")} data-testid="folder-all" className={`w-full text-left flex items-center justify-between px-2 py-2 rounded-md text-sm lift ${folderId === "" ? "bg-accent text-accent-foreground" : "hover:bg-secondary"}`}>
                <span className="flex items-center gap-2"><FileText className="size-4" /> All</span>
                <span className="text-xs text-muted-foreground">{resumes.length}</span>
              </button>
              {folders.map((f) => (
                <button key={f.id} onClick={() => setFolderId(f.id)} data-testid={`folder-${f.id}`} className={`w-full text-left flex items-center gap-2 px-2 py-2 rounded-md text-sm lift ${folderId === f.id ? "bg-accent text-accent-foreground" : "hover:bg-secondary"}`}>
                  <Folder className="size-4" style={{ color: f.color }} />
                  <span className="truncate">{f.name}</span>
                </button>
              ))}
              {folders.length === 0 && <div className="text-xs text-muted-foreground mt-2">No folders yet</div>}
            </CardContent>
          </Card>

          {allTags.length > 0 && (
            <Card className="border-border mt-3">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Tags</div>
                <div className="flex flex-wrap gap-1.5">
                  <button onClick={() => setTag("")} className={`chip lift ${!tag ? "bg-primary text-primary-foreground" : ""}`} data-testid="tag-all">All</button>
                  {allTags.map((t) => (
                    <button key={t} onClick={() => setTag(t)} className={`chip lift ${tag === t ? "bg-primary text-primary-foreground" : ""}`} data-testid={`tag-${t}`}>{t}</button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </aside>

        {/* List */}
        <section className="col-span-12 md:col-span-9">
          <div className="flex gap-2 items-center mb-3 flex-wrap">
            <div className="relative flex-1 min-w-[260px]">
              <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search across name, role, summary, company, skills, education…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 h-10"
                data-testid="organizer-search"
                title="Smart search — looks across candidate name, role, summary, company names, skills/competencies, education and client."
              />
              {q && (
                <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs" data-testid="organizer-search-clear">Clear</button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[170px] h-10" data-testid="organizer-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger className="w-[150px] h-10" data-testid="organizer-level-filter"><SelectValue placeholder="All levels" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All levels</SelectItem>
                <SelectItem value="fresher">Fresher</SelectItem>
                <SelectItem value="junior">Junior</SelectItem>
                <SelectItem value="mid">Mid-level</SelectItem>
                <SelectItem value="senior">Senior</SelectItem>
                <SelectItem value="lead">Lead</SelectItem>
                <SelectItem value="executive">Executive</SelectItem>
              </SelectContent>
            </Select>
            <div className="inline-flex rounded-md border border-border overflow-hidden">
              <button onClick={() => setView("grid")} className={`px-3 h-10 text-xs font-semibold inline-flex items-center gap-1.5 ${view === "grid" ? "bg-secondary" : "bg-card"}`} data-testid="organizer-view-grid"><LayoutGrid className="size-4" /> Grid</button>
              <button onClick={() => setView("kanban")} className={`px-3 h-10 text-xs font-semibold inline-flex items-center gap-1.5 border-l border-border ${view === "kanban" ? "bg-secondary" : "bg-card"}`} data-testid="organizer-view-kanban"><KanbanSquare className="size-4" /> Pipeline</button>
            </div>
            <Button variant="outline" className="gap-2 lift h-10" data-testid="organizer-filter-button"><Filter className="size-4" /> Tag</Button>
          </div>

          {resumes.length === 0 ? (
            <div className="border border-dashed border-border rounded-xl p-12 text-center">
              <img src="https://static.prod-images.emergentagent.com/jobs/c62a2689-874e-42f8-9ec1-502cc536b43a/images/03c6a4e117ddc626e42c601a9203839fc5a769de4636276e4e7c702d7ea51f34.png" alt="" className="size-32 object-contain mx-auto opacity-90" />
              <div className="display font-bold text-lg mt-4">No resumes yet</div>
              <div className="text-sm text-muted-foreground mt-1">Drop a file in the Upload screen to begin.</div>
              <Button className="mt-4 lift" onClick={() => navigate("/upload")} data-testid="empty-upload-cta">Upload one now</Button>
            </div>
          ) : view === "kanban" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="organizer-kanban">
              {STATUSES.map((col) => {
                const colItems = resumes.filter((r) => (r.pipeline_status || "draft") === col.key);
                return (
                  <div key={col.key} className="bg-secondary/50 rounded-lg p-2 min-h-[200px]" data-testid={`kanban-col-${col.key}`}>
                    <div className="flex items-center justify-between px-1 mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`size-2 rounded-full ${col.color}`} />
                        <span className="text-[10px] uppercase tracking-[0.18em] font-bold">{col.label}</span>
                      </div>
                      <span className="text-xs text-muted-foreground font-semibold">{colItems.length}</span>
                    </div>
                    <div className="space-y-2">
                      {colItems.map((r) => (
                        <Card key={r.id} className="border-border lift hover:border-primary/40 cursor-pointer" onClick={() => navigate(`/editor/${r.id}`)} data-testid={`kanban-card-${r.id}`}>
                          <CardContent className="p-3">
                            <div className="font-bold text-sm truncate display">{r.data?.name || r.title}</div>
                            <div className="text-[11px] text-muted-foreground truncate">{r.data?.title || "Untitled role"}</div>
                            <div className="mt-2 flex flex-wrap gap-1">
                              {(r.data?.competencies || []).slice(0, 2).map((c, i) => (<span key={i} className="chip text-[9px]">{c}</span>))}
                            </div>
                            <Select value={r.pipeline_status || "draft"} onValueChange={(v) => setStatus(r.id, v)}>
                              <SelectTrigger onClick={(e) => e.stopPropagation()} className="mt-2 h-7 text-[10px]" data-testid={`kanban-status-${r.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </CardContent>
                        </Card>
                      ))}
                      {colItems.length === 0 && <div className="text-[11px] text-muted-foreground text-center py-6">Empty</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {visibleResumes.map((r) => {
                const st = STATUSES.find((s) => s.key === (r.pipeline_status || "draft")) || STATUSES[0];
                return (
                  <Card key={r.id} className="border-border lift hover:border-primary/40" data-testid={`resume-card-${r.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <button onClick={() => navigate(`/editor/${r.id}`)} className="text-left min-w-0 flex-1">
                          <div className="font-bold truncate display">{r.data?.name || r.title}</div>
                          <div className="text-xs text-muted-foreground truncate">{r.data?.title || "Untitled role"}</div>
                        </button>
                        <Button size="icon" variant="ghost" onClick={() => del(r.id)} data-testid={`resume-delete-${r.id}`} className="text-destructive">
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1">
                        {(r.data?.competencies || []).slice(0, 4).map((c, i) => (
                          <span key={i} className="chip">{c}</span>
                        ))}
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <Select value={r.pipeline_status || "draft"} onValueChange={(v) => setStatus(r.id, v)}>
                          <SelectTrigger className="h-7 text-[10px] w-auto min-w-[120px] gap-1.5 px-2" data-testid={`resume-status-${r.id}`}>
                            <span className={`size-1.5 rounded-full ${st.color} shrink-0`} />
                            <span className="uppercase tracking-wider font-bold">{st.label}</span>
                          </SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <span className="text-[10px] text-muted-foreground">{new Date(r.updated_at).toLocaleDateString()}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {visibleResumes.length === 0 && (
                <div className="col-span-full text-center py-12 text-muted-foreground text-sm">No resumes match this status filter</div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
